import { useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import MKBox from 'components/MaterialKit/MKBox';
import MKTypography from 'components/MaterialKit/MKTypography';
import InputField from 'components/InputField';
import Button from 'components/Button';
import { getLocaleMap } from 'utils/locales';
import { useNavigate } from 'react-router-dom';
import Select from 'components/Select';

const EditBrandForm = ({ brand, onSave,
  editButtonColor,
  editFontColor,
  saveButtonColor,
  saveButtonFontColor,
  backButtonColor,
  backButtonFontColor,
  ...props
}) => {
  const [lm, setLm] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    getLocaleMap([
      'errors_required',
      'save_button',
    ])
      .then((response) => {
        setLm(response);
      });
  }, []);

  const getFormValidation = (values) => {
    const { name } = values;
    const errors = {};
    if (!name) {
      errors.name = lm.errors_required;
    }
    return errors;
  };

  const initialValues = useMemo(() => ({
    name: brand?.name || '',
    display_status: brand?.display_status || true,
  }), [brand]);

  return (
    <Formik
      onSubmit={onSave}
      initialValues={initialValues}
      validate={getFormValidation}
      enableReinitialize
      {...props}
    >
      {({ handleChange, handleBlur, handleSubmit, setFieldValue, errors, values, touched, isSubmitting, dirty }) => {
        return (
          <MKBox component="form" role="form" onSubmit={handleSubmit}>
            <Grid container spacing={1}>
              <Grid item xs={12} mt={2}>
                <MKBox display="flex" width="100%" justifyContent="space-between">
                  <Button
                    color={backButtonColor || 'info'}
                    disabled={isSubmitting}
                    style={{
                      border: backButtonColor === 'white' && '1px solid black',
                      width: '10vw',
                    }}
                    onClick={() => { navigate(-1); }}
                    sx={{
                      color: backButtonFontColor,
                    }}
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    color={saveButtonColor}
                    disabled={isSubmitting || !dirty}
                    style={{
                      width: '10vw',
                    }}
                    sx={{
                      color: saveButtonFontColor,
                    }}
                  >
                    {isSubmitting ? (
                      <CircularProgress color="white" size={24} sx={{ position: 'absolute' }} />
                    ) : lm.save_button}
                  </Button>
                </MKBox>
                <MKTypography variant="caption" color="error">
                  {errors.form}
                  &nbsp;
                </MKTypography>
              </Grid>
              <Grid container display="flex" flexDirection="row" alignItems="center">
                <Grid item xs={4}>
                  <MKTypography color="black">
                    Brand Name
                  </MKTypography>
                </Grid>
                <Grid item xs={8}>
                  <InputField
                    name="name"
                    label=""
                    value={values.name}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    error={touched.name && !!errors.name}
                  />
                </Grid>
              </Grid>
              <MKTypography sx={{ ml: 1, mt: 0.5 }} variant="caption" color="error">
                {(touched.name && !!errors.name) ? errors.name : '\xa0'}
              </MKTypography>
              <Grid container display="flex" flexDirection="row" alignItems="center">
                <Grid item xs={4}>
                  <MKTypography color="black">
                    Display Status
                  </MKTypography>
                </Grid>
                <Grid item xs={8}>
                  <Select
                    name="display_status"
                    value={values.display_status}
                    onChange={(v) => setFieldValue('display_status', v)}
                    options={[
                      { label: 'Active', value: true },
                      { label: 'Inactive', value: false },
                    ]}
                    fullWidth
                  />
                </Grid>
              </Grid>
              <MKTypography sx={{ ml: 1, mt: 0.5 }} variant="caption" color="error">
                {(touched.display_status && !!errors.display_status) ? errors.display_status : '\xa0'}
              </MKTypography>
            </Grid>
          </MKBox>
        );
      }}
    </Formik>
  );
};

EditBrandForm.propTypes = {
  brand: PropTypes.object,
  onSave: PropTypes.func,
  editButtonColor: PropTypes.string,
  editFontColor: PropTypes.string,
  saveButtonColor: PropTypes.string,
  saveButtonFontColor: PropTypes.string,
  backButtonColor: PropTypes.string,
  backButtonFontColor: PropTypes.string,
};

EditBrandForm.defaultProps = {
  brand: {},
  onSave: () => {},
};

export default EditBrandForm;
